import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { FormContextValue, FormData } from "./types";
import {
  getStoredFormData,
  saveFormData,
  clearStoredFormData,
  PREVIEW_STORAGE_KEY,
} from "./formStorage";
import {
  isPreferredVoice,
  resolvePreferredVoice,
  type PreferredVoice,
} from "./preferredVoice";
import { DEFAULT_CHARACTER } from "@/constants/character";
import { DEFAULT_AUDIO_VOICE } from "@/constants/audioVoice";
import { getFlagIcon } from "@/constants/multilingual";
import { TutorMode } from "@/constants/mode";
import { useAuthStore } from "@/store/useAuthStore";

const FormContext = createContext<FormContextValue | undefined>(undefined);

export const initialFormData: FormData = {
  language: {
    label: "English (Default)",
    value: "en",
    icon: getFlagIcon("en"),
    multilingual: false,
  },
  character: {
    characterId: DEFAULT_CHARACTER.id,
  },
  voice: {
    voiceId: DEFAULT_AUDIO_VOICE.id,
    speed: DEFAULT_AUDIO_VOICE.defaultSpeed,
  },
};

interface FormProviderProps {
  children: React.ReactNode;
  mode?: TutorMode;
}

/** Returns `base` untouched unless the preference resolves to an offered voice. */
const applyPreferredVoice = (
  base: FormData,
  pref: unknown,
  includeBeta: boolean,
): FormData => {
  if (!isPreferredVoice(pref)) return base;
  const resolved = resolvePreferredVoice(pref as PreferredVoice, {
    includeBeta,
  });
  if (!resolved) return base;
  return {
    ...base,
    voice: { voiceId: resolved.voice.id, speed: pref.speed },
    language: resolved.language,
  };
};

export const FormProvider = ({
  children,
  mode = TutorMode.PUBLIC,
}: FormProviderProps) => {
  const isPreview = mode === TutorMode.PREVIEW;
  // Preview keeps its own key so studio choices never touch live settings;
  // undefined falls through to the live key.
  const storageKey = isPreview ? PREVIEW_STORAGE_KEY : undefined;

  // Declared before useState so its initializer can mark an already-applied
  // preference as seeded, avoiding a redundant setFormData from the effect below.
  const seededRef = useRef(false);

  const [formData, setFormData] = useState<FormData>(() => {
    const cached = getStoredFormData(initialFormData, storageKey);
    if (isPreview) return cached;
    // Readable synchronously on a repeat load: setUserConfig persists
    // user.config to localStorage, so this does not await the fetch.
    const state = useAuthStore.getState();
    const applied = applyPreferredVoice(
      cached,
      state.user?.config?.preferredVoice,
      Boolean(state.user?.config?.enableBetaTtsProviders),
    );
    // Unchanged (applied === cached) means nothing resolved yet — leave
    // seededRef false so the effect can still apply it once betaEnabled etc. arrive.
    if (applied !== cached) seededRef.current = true;
    return applied;
  });

  // The primary seed path on a fresh browser, where nothing is cached and the
  // preference only arrives with hydrateUserConfig after mount.
  const preferredVoice = useAuthStore(
    (state) => state.user?.config?.preferredVoice,
  );
  const betaEnabled = useAuthStore(
    (state) => state.user?.config?.enableBetaTtsProviders,
  );
  useEffect(() => {
    if (isPreview || seededRef.current || !preferredVoice) return;
    seededRef.current = true;
    setFormData((prev) =>
      applyPreferredVoice(prev, preferredVoice, Boolean(betaEnabled)),
    );
  }, [isPreview, preferredVoice, betaEnabled]);

  useEffect(() => {
    saveFormData(formData, storageKey);
  }, [formData, storageKey]);

  const updateField = useCallback(
    <K extends keyof FormData>(field: K, value: FormData[K]) => {
      // Closes the seed window, but only for what the seed writes: a late
      // server value must not land on a voice or language just chosen here.
      // Character is not part of the record, so changing it must not disarm
      // the seed — that left a fresh browser on the default voice.
      if (field === "voice" || field === "language") {
        seededRef.current = true;
      }
      setFormData((prev) => ({ ...prev, [field]: value }));
    },
    [],
  );

  const reset = useCallback(() => {
    setFormData(initialFormData);
    clearStoredFormData(storageKey);
  }, [storageKey]);

  return (
    <FormContext.Provider
      value={{
        formData,
        setFormData,
        updateField,
        reset,
        mode,
      }}
    >
      {children}
    </FormContext.Provider>
  );
};

export const useForm = () => {
  const context = useContext(FormContext);
  if (context === undefined) {
    throw new Error("useSessionForm must be used within a SessionFormProvider");
  }
  return context;
};
